///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Sentences for the storm column HUD, derived from the redux state. Pure
/// so the HUD stays a thin renderer. Every modeled quantity says so.
///

import type { StormCutoutBestTrack, StormCutoutBuoy, StormCutoutEnvironment, StormCutoutForecast, StormCutoutHeadline, StormCutoutLightning, StormCutoutShips, StormCutoutStructure, StormCutoutSummary, StormCutoutWind } from "@/redux/dialogSlice"
import { knaffZehrPressureHpa } from "@/earthbrowser/dataset-distribution/hurricanes/column/stormColumnStats"
import { type ClosestApproach } from "@/earthbrowser/dataset-distribution/hurricanes/stormInsights"
import { type WindProbability } from "@/earthbrowser/dataset-distribution/hurricanes/HurricanesTable"
import { maxProbability, probabilityAt, thresholdLabel, type WindProbabilityThreshold } from "@/earthbrowser/dataset-distribution/hurricanes/windProbability"
import { convertSpeed, formatDistance, formatSpeed, getSpeedUnit } from "@/earthbrowser/utils/unitFormatters"

const cKnotsToMps = 0.514444
/** NHC definition of rapid intensification: ≥ 30 kt in 24 h */
export const cRapidIntensificationKt = 30

export function pad3(deg: number): string {
	return String(Math.round(((deg % 360) + 360) % 360)).padStart(3, "0")
}

export function speedKt(kt: number): string {
	return formatSpeed(kt * cKnotsToMps)
}

export function speedValueKt(kt: number): string {
	return `${Math.round(convertSpeed(kt * cKnotsToMps))}`
}

export function speedUnit(): string {
	return getSpeedUnit()
}

/** Display-unit value of one knot (for picking round chart ticks) */
export function displayPerKt(): number {
	return convertSpeed(100 * cKnotsToMps, 3) / 100
}

/** "+20 kt in 24 h" / "−15 kt in 24 h" / "steady over 24 h" */
export function changeSentence(h: StormCutoutHeadline): string | undefined {
	if (h.change24hKt === null) return undefined
	if (Math.abs(h.change24hKt) < 5) return "steady over 24 h"
	const sign = h.change24hKt > 0 ? "+" : "−"
	return `${sign}${speedValueKt(Math.abs(h.change24hKt))} ${speedUnit()} in 24 h`
}

export function isRapidIntensification(h: StormCutoutHeadline): boolean {
	return h.change24hKt !== null && h.change24hKt >= cRapidIntensificationKt
}

/** Block dimensions and the printed scales */
export function blockSentence(s: StormCutoutSummary): string {
	const parts = [`${s.lengthKm} km along motion ${pad3(s.motionDeg)}°`, `${s.widthKm} km across`, `height ×${s.heightScale}`]
	return parts.join(" · ")
}

/** The IR frames behind the lid */
export function framesSentence(s: StormCutoutSummary): string {
	const f = s.frames
	if (!f) return "No storm-centered IR frames for this storm yet (too close to the satellite limb, or not yet cut)"
	const hours = Math.max(0, (Date.parse(f.newestIso) - Date.parse(f.oldestIso)) / 3.6e6)
	const span = hours < 1 ? `${Math.round(hours * 60)} min` : `${Math.round(hours)} h`
	return `${f.count} ${f.satellite} IR frame${f.count === 1 ? "" : "s"} over ${span} · showing ${formatUtc(f.displayedIso)} · heights from a 6.5 K/km lapse rate, ±1–2 km`
}

/** Eye, coldest tops, deep-convection fraction */
export function structureSentences(st: StormCutoutStructure, deckEyeKm?: number | null): string[] {
	const lines: string[] = []
	if (st.eyeDiameterKm !== undefined) {
		const deck = deckEyeKm ? ` · NHC best track says ${formatDistance(deckEyeKm)}` : ""
		lines.push(`Eye about ${formatDistance(st.eyeDiameterKm)} across (IR estimate, ${st.eyeContrastK} K warmer than the eyewall)${deck}`)
	} else {
		lines.push(deckEyeKm
			? `No warm eye resolved in the IR field, though the best track carries a ${formatDistance(deckEyeKm)} eye (cirrus-filled or under the frame's resolution)`
			: "No warm eye resolved in the IR field")
	}
	// 200 K is the floor of the frame encoding; colder tops saturate there
	lines.push(st.coldestTopC <= -72.5
		? `Coldest tops at or below −73 °C (the frame's floor) ≈ ${st.coldestTopKm} km or higher`
		: `Coldest tops ${st.coldestTopC} °C ≈ ${st.coldestTopKm} km`)
	const trend = coldFractionTrend(st)
	lines.push(`Tops colder than −70 °C over ${(st.coldFraction * 100).toFixed(1)}% of the frame${trend ? ` · ${trend}` : ""}`)
	return lines
}

/** Direction of the deep-convection fraction over the loaded frames */
function coldFractionTrend(st: StormCutoutStructure): string | undefined {
	const series = st.coldFractionTrend
	if (series.length < 3) return undefined
	const first = series[0]
	const last = series[series.length - 1]
	const hours = (last.t - first.t) / 3.6e6
	if (hours < 0.5) return undefined
	const delta = last.fraction - first.fraction
	const span = hours < 1.5 ? `${Math.round(hours * 60)} min` : `${Math.round(hours)} h`
	if (Math.abs(delta) < 0.01) return `flat over ${span}`
	return `${delta > 0 ? "growing" : "shrinking"} over ${span}`
}

/** The parametric wind field and what it was fitted to */
export function windSentences(w: StormCutoutWind, h: StormCutoutHeadline): string[] {
	const lines: string[] = []
	const fit = w.source === "fit"
		? `Holland profile fitted to the advisory's 34/50/64 kt radii`
		: `Holland profile with a climatological shape (no wind radii in this advisory)`
	const rmw = w.rmwSource === "atcf" ? `RMW ${formatDistance(w.rmwKm)} from the NHC best track` : `RMW ${formatDistance(w.rmwKm)} from climatology`
	lines.push(`${fit} · ${rmw} · B ${w.b.map(b => b.toFixed(1)).join(" / ")} by quadrant · field modeled, not observed`)
	if (w.bestTrack) lines.push(...bestTrackSentences(w.bestTrack, h))
	if (w.r34MeanKm !== undefined) {
		const asym = w.asymmetry !== undefined && w.asymmetry >= 1.3 ? ` · ${w.asymmetry}× asymmetric` : ""
		lines.push(`34-kt winds extend ${formatDistance(w.r34MeanKm)} on average${asym}`)
	}
	if (w.ikeTJ !== undefined) {
		lines.push(`Integrated kinetic energy ${w.ikeTJ} TJ (${ikeContext(w.ikeTJ, h.category)})`)
	}
	return lines
}

/**
 * What the ATCF best track says about the core: eyewall contraction or
 * expansion over 12 / 24 h (a contracting RMW accompanies intensification;
 * a sudden jump outward is the signature of an eyewall replacement), the
 * eye diameter, and the outer closed isobar as the pressure footprint
 */
export function bestTrackSentences(bt: StormCutoutBestTrack, h: StormCutoutHeadline): string[] {
	const lines: string[] = []
	if (bt.rmwKm !== null) {
		const ref = bt.rmw12hKm ?? bt.rmw24hKm
		const refHours = bt.rmw12hKm !== null ? 12 : 24
		if (ref !== null && ref > 0) {
			const ratio = bt.rmwKm / ref
			if (ratio >= 1.2) {
				lines.push(`Eyewall widened from ${formatDistance(ref)} to ${formatDistance(bt.rmwKm)} in ${refHours} h${ratio >= 1.4 && h.category >= 2 ? " · an outward jump like this is how an eyewall replacement reads in the best track" : ""}`)
			} else if (ratio <= 0.8) {
				lines.push(`Eyewall contracting: RMW ${formatDistance(ref)} → ${formatDistance(bt.rmwKm)} in ${refHours} h · contraction accompanies intensification`)
			} else {
				lines.push(`RMW steady near ${formatDistance(bt.rmwKm)} over ${refHours} h`)
			}
		}
	}
	const core: string[] = []
	if (bt.eyeKm !== null) core.push(`eye ${formatDistance(bt.eyeKm)} across (best track)`)
	if (bt.rociKm !== null) core.push(`outer closed isobar ${bt.pociMb !== null ? `${Math.round(bt.pociMb)} hPa ` : ""}at ${formatDistance(bt.rociKm)} · ring on the floor`)
	if (bt.depth === "D") core.push("deep system")
	else if (bt.depth === "S") core.push("shallow system")
	if (core.length > 0) lines.push(capitalize(core.join(" · ")))
	return lines
}

/** Rough IKE context against typical values (Powell & Reinhold 2007) */
function ikeContext(tj: number, category: number): string {
	if (tj < 20) return "small field"
	if (tj < 60) return "typical for a hurricane of this size"
	if (tj < 120) return category >= 3 ? "large wind field" : "unusually large field for the category"
	return "very large wind field, Katrina/Sandy class"
}

/** Forecast peak and landfall timing */
export function forecastSentence(f: StormCutoutForecast, h: StormCutoutHeadline): string | undefined {
	const parts: string[] = []
	if (f.peak && h.maxWindKt !== null && f.peak.kt > h.maxWindKt + 2) {
		parts.push(`forecast peak ${speedKt(f.peak.kt)} ${formatTimeUntil(f.peak.t)}`)
	} else if (f.peak && h.maxWindKt !== null) {
		parts.push("at or near peak intensity now")
	}
	if (f.landfallT !== undefined) parts.push(`landfall ${formatLocal(f.landfallT)}`)
	if (f.ensembleRun) parts.push(`ECMWF ENS ${formatRun(f.ensembleRun)} (band; model winds run low against NHC Vmax)`)
	if (f.ships) parts.push("SHIPS statistical forecast dotted")
	return parts.length > 0 ? capitalize(parts.join(" · ")) : undefined
}

/** Sea surface temperature under and ahead of the storm, and the wake behind it */
export function environmentSentences(e: StormCutoutEnvironment): string[] {
	const lines: string[] = []
	if (e.sstUnderCenterC !== undefined) {
		const warm = e.sstUnderCenterC >= 26.5
		lines.push(`Sea surface ${e.sstUnderCenterC.toFixed(1)} °C under the center${warm ? "" : " · below the 26.5 °C that sustains a tropical cyclone"}`)
	} else {
		lines.push("No sea surface temperature under the center (over land)")
	}
	if (e.warmWaterEndsT !== undefined) {
		const hours = Math.round((e.warmWaterEndsT - Date.now()) / 3.6e6)
		lines.push(hours <= 0
			? "The forecast track is leaving water warm enough to sustain the storm now"
			: `Warm water (≥ 26.5 °C) ends along the forecast track ${formatLocal(e.warmWaterEndsT)} · in about ${hours} h`)
	} else if (!e.alreadyCool && e.sstUnderCenterC !== undefined) {
		lines.push("Warm water (≥ 26.5 °C) along the whole forecast track")
	}
	if (e.coldWakeC !== undefined && e.wakeSpanHours !== undefined) {
		const wake = e.coldWakeC <= -0.3
			? `Cold wake: water along the last day of track cooled ${Math.abs(e.coldWakeC).toFixed(1)} °C over ${e.wakeSpanHours} h`
			: `No cold wake yet along the last day of track (${e.coldWakeC >= 0 ? "+" : ""}${e.coldWakeC.toFixed(1)} °C over ${e.wakeSpanHours} h)`
		lines.push(wake)
	}
	lines.push(`CoralTemp daily analysis for ${new Date(e.frameIso).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" })}`)
	return lines
}

/** The SHIPS checklist: shear, humidity, heat content, potential intensity, RI odds */
export function shipsSentences(s: StormCutoutShips): string[] {
	const lines: string[] = []
	if (s.shearKt !== null) {
		const heading = s.shearDirDeg !== null ? ` toward ${cardinal(s.shearDirDeg)} (${pad3(s.shearDirDeg)}°)` : ""
		const trend = s.shear24Kt !== null && Math.abs(s.shear24Kt - s.shearKt) >= 5
			? ` · ${s.shear24Kt > s.shearKt ? "rising" : "easing"} to ${speedKt(s.shear24Kt)} by +24 h`
			: ""
		lines.push(`Deep-layer shear ${speedKt(s.shearKt)}${heading} · ${shearVerdict(s.shearKt)}${trend}`)
	}
	const env: string[] = []
	if (s.rhMidPct !== null) env.push(`mid-level humidity ${Math.round(s.rhMidPct)}%${s.rhMidPct < 50 ? " (dry)" : ""}`)
	if (s.oceanHeatContent !== null) env.push(`ocean heat content ${Math.round(s.oceanHeatContent)} kJ/cm²${s.oceanHeatContent >= 60 ? " (deep warm layer)" : s.oceanHeatContent < 20 ? " (shallow)" : ""}`)
	if (s.potentialIntensityKt !== null) env.push(`potential intensity ${speedKt(s.potentialIntensityKt)}`)
	if (env.length > 0) lines.push(capitalize(env.join(" · ")))
	const ri = s.ri.find(r => r.thresholdKt === 30 && r.hours === 24) ?? s.ri.find(r => r.hours === 24) ?? s.ri[0]
	if (ri) {
		const clim = ri.climatologyPct !== null ? ` (climatology ${Math.round(ri.climatologyPct)}%)` : ""
		lines.push(`Rapid intensification odds ${Math.round(ri.consensusPct)}% for +${speedKt(ri.thresholdKt)} in ${ri.hours} h${clim} · SHIPS-RII consensus, ${formatRun(s.run)}`)
	}
	return lines
}

function shearVerdict(kt: number): string {
	if (kt < 10) return "very low shear"
	if (kt < 20) return "favorable"
	if (kt < 30) return "marginal"
	return "hostile"
}

function cardinal(degrees: number): string {
	const directions = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"]
	return directions[Math.round(((degrees % 360) + 360) % 360 / 22.5) % 16]
}

/** One line per buoy pin, nearest first */
export function buoySentence(b: StormCutoutBuoy): string {
	const parts = [`${b.name || b.stationId} · ${formatDistance(b.distanceKm)} from the center`]
	if (b.pressureHpa !== null) parts.push(`${Math.round(b.pressureHpa)} hPa`)
	if (b.windGustMps !== null) parts.push(`gusts ${speedKt(b.windGustMps / cKnotsToMps)}`)
	else if (b.windSpeedMps !== null) parts.push(`wind ${speedKt(b.windSpeedMps / cKnotsToMps)}`)
	if (b.waveHeightM !== null) parts.push(`seas ${b.waveHeightM.toFixed(1)} m`)
	if (b.waterTempC !== null) parts.push(`water ${b.waterTempC.toFixed(1)} °C`)
	if (parts.length === 1) parts.push(b.time ? "no meteorological report" : "no recent report")
	return parts.join(" · ")
}

/**
 * GLM lightning in the block: the hour to the clock against the hour
 * before, how much of it is inner core, and what that usually means.
 * Inner-core bursts tend to precede intensification by 6–24 h (DeMaria et
 * al. 2012); rainband lightning is routine, and a strong steady-state
 * hurricane's eyewall is often quiet
 */
export function lightningSentences(l: StormCutoutLightning, h: StormCutoutHeadline): string[] {
	if (!l.covered) {
		return ["No GLM coverage: the GOES lightning mappers see the Americas and the oceans on either side; Himawari carries none"]
	}
	if (l.total === 0) {
		return ["No GLM flashes in the published window — a quiet storm, or the shard hasn't caught up yet"]
	}
	const lines: string[] = []
	const at = formatUtc(new Date(l.referenceT).toISOString())
	const head = l.lastHour === 0
		? `No flashes in the hour to ${at}`
		: `${l.lastHour} flash${l.lastHour === 1 ? "" : "es"} in the hour to ${at}`
	const trend = l.priorHour === 0 && l.lastHour === 0
		? "quiet for two hours"
		: l.priorHour === 0
			? "none the hour before"
			: l.lastHour >= l.priorHour * 1.5
				? `up from ${l.priorHour} the hour before`
				: l.lastHour <= l.priorHour * 0.67
					? `down from ${l.priorHour} the hour before`
					: `about the same as the hour before (${l.priorHour})`
	lines.push(`${head} · ${trend}`)
	if (l.lastHour > 0) {
		const share = l.innerCoreLastHour / l.lastHour
		const where = share >= 0.5
			? `${Math.round(share * 100)}% inside the inner core (within ${formatDistance(l.innerCoreKm)} of the center)`
			: share > 0
				? `${Math.round(share * 100)}% inner core, the rest in the rainbands`
				: "all of it in the rainbands, none in the inner core"
		lines.push(capitalize(where))
		const intensifying = h.change24hKt !== null && h.change24hKt >= 10
		if (share >= 0.5 && l.lastHour >= 20) {
			lines.push(intensifying
				? "Eyewall burst while strengthening: inner-core lightning like this tends to lead intensification by 6–24 h"
				: "Eyewall burst: inner-core lightning tends to precede intensification by 6–24 h, or a struggling core being sheared")
		} else if (share < 0.25) {
			lines.push(h.category >= 1
				? "Rainband lightning is routine; a mature hurricane's eyewall is often quiet"
				: "Lightning away from the center: the convection is displaced from the circulation, as in a sheared storm")
		}
	}
	if (l.peak && l.peak.count > l.lastHour * 1.5 && l.peak.count >= 10) {
		lines.push(`Peak ${l.peak.count}/h in the hour to ${formatLocal(l.peak.t)} · ${l.total} flashes over ${Math.round(l.spanHours)} h`)
	} else {
		lines.push(`${l.total} flashes over ${Math.round(l.spanHours)} h`)
	}
	return lines
}

/**
 * NHC wind speed probabilities in words: what the swath shows, the odds at
 * the nearest city for all three thresholds, and the peak of the chosen
 * one. These fold track, intensity and size uncertainty together, which
 * the deterministic arrival time in the Hazards row does not
 */
export function windProbabilitySentences(wp: WindProbability, kt: WindProbabilityThreshold, approach: ClosestApproach | null, h: StormCutoutHeadline): string[] {
	const lines: string[] = []
	const peak = maxProbability(wp, kt)
	lines.push(`Chance of ${thresholdLabel(kt).toLowerCase()} winds within 5 days · issued ${formatRun(wp.issued)} · ${peak > 0 ? `peaks at ${peak}% along the track` : "under 5% everywhere"}`)
	if (approach) {
		const p34 = probabilityAt(wp, 34, approach.cityLat, approach.cityLon)
		const p50 = probabilityAt(wp, 50, approach.cityLat, approach.cityLon)
		const p64 = probabilityAt(wp, 64, approach.cityLat, approach.cityLon)
		if (p34 === 0) {
			lines.push(`${approach.cityName}: under 5% for tropical-storm-force winds`)
		} else {
			const parts = [`${p34}% tropical-storm force`]
			if (p50 > 0) parts.push(`${p50}% for 50 kt`)
			parts.push(p64 > 0 ? `${p64}% hurricane force` : "under 5% hurricane force")
			lines.push(`${approach.cityName}: ${parts.join(" · ")}`)
		}
	}
	const basin = h.basin.toUpperCase()
	if (basin === "AL" || basin === "EP" || basin === "CP") {
		lines.push("Monte Carlo over NHC's historical track, intensity and size errors (1,000 realizations); combined over every active storm")
	}
	return lines
}

/** How the current wind–pressure pair sits against the climatological curve */
export function windPressureSentence(h: StormCutoutHeadline): string | undefined {
	if (h.maxWindKt === null || h.mslpMb === null) return undefined
	const expected = knaffZehrPressureHpa(h.maxWindKt, h.lat, h.movementSpeedKt ?? 0, h.basin)
	const delta = Math.round(h.mslpMb - expected)
	if (Math.abs(delta) < 4) return `Pressure ${Math.round(h.mslpMb)} hPa is on the Knaff–Zehr curve for ${speedKt(h.maxWindKt)} at this latitude`
	return delta < 0
		? `Pressure ${Math.abs(delta)} hPa lower than the Knaff–Zehr curve expects for ${speedKt(h.maxWindKt)}: a large, deep pressure field for its winds`
		: `Pressure ${delta} hPa higher than the Knaff–Zehr curve expects for ${speedKt(h.maxWindKt)}: a compact storm, intense for its pressure`
}

export function categoryLabel(category: number): string {
	if (category < 0) return "below TS"
	if (category === 0) return "TS"
	return `Cat ${category}`
}

export function formatLocal(tMs: number): string {
	const d = new Date(tMs)
	return `${d.toLocaleDateString(undefined, { weekday: "short" })} ${d.toLocaleTimeString(undefined, { hour: "numeric" })}`
}

export function formatTimeUntil(tMs: number): string {
	const hours = Math.round((tMs - Date.now()) / 3.6e6)
	if (hours <= 0) return "now"
	if (hours < 24) return `in ${hours} h`
	return `in ${Math.round(hours / 24)} d`
}

function formatUtc(iso: string): string {
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return iso
	return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}Z`
}

export function formatRun(runIso: string): string {
	const d = new Date(runIso)
	if (Number.isNaN(d.getTime())) return runIso
	return `${String(d.getUTCHours()).padStart(2, "0")}Z ${d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })}`
}

/** When the next NHC advisory is due: full advisories at 03/09/15/21Z, intermediates in between */
export function nextAdvisorySentence(h: StormCutoutHeadline): string | undefined {
	const basin = h.basin.toUpperCase()
	if (basin !== "AL" && basin !== "EP" && basin !== "CP") return undefined
	const now = new Date()
	const hour = now.getUTCHours()
	const slots = [0, 3, 6, 9, 12, 15, 18, 21]
	const next = slots.find(s => s > hour) ?? 24
	const due = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), next, 0, 0))
	const full = (next % 6) === 3
	return `Next ${full ? "advisory" : "intermediate advisory"} ${due.toLocaleTimeString(undefined, { hour: "numeric" })}`
}

function capitalize(s: string): string {
	return s.charAt(0).toUpperCase() + s.slice(1)
}
